import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createBlock as _createBlock, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-186b2b82"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "fas fa-spinner fa-spin" }, null, -1))
const _hoisted_3 = [
  _hoisted_2
]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "btn btn-sm btn-outline-secondary floorplan-upload-button" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "fas fa-plus" }, null, -1))

import { computed, ref, onMounted, inject,PropType } from 'vue';
import { useStore } from 'vuex';
import { useToast } from "vue-toastification";
import _ from "lodash";
import moment from "moment-timezone";
import sanitize from "sanitize-filename";
import slugify from "slugify";
import { Storage } from 'aws-amplify'
import { Document } from "@/models";
import FloorplanComponent from "@/components/floorplan/Floorplan.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Floorplans',
  props: {
  path: {type: String as PropType<string>, default: ''},
  floorplan: {type: Object as PropType<Document[]>, default: []},
  buttontext: {type: String as PropType<string>, default: 'Document'},
  entity: {type: String as PropType<string>, default: 'customer'},
  readonly: { type: Boolean as PropType<boolean>, required: false, default: false }
},
  emits: ['clicked'],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit
const { path, floorplan, buttontext } = props;
const store = useStore();
const toasted = useToast();
const actProperty: any = inject('actProperty');

// Refs for reactive properties
const file = ref(null);
const isDragging = ref(false);
const uploadingCount = ref(0);

const deviceKey = computed((): string => store.getters['auth/deviceKey']);
const propertyspec = computed((): string => store.getters['diary/propertyspec']);

// Methods converted to anonymous functions
const setPropertyspecDeep = async (payload: { path: string; data: any; }) => {
  await store.dispatch('diary/setPropertyspecDeep', payload);
};
const validtypes = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg'
];

/**
 * Delete a Floorplan.
 *
 * Note this only removes the Floorplan reference from the data in Vuex.
 * It DOES NOT delete the actual file from file storage (e.g. from FTP/S3).
 *
 * @param {number} index Index of Floorplan to delete
 */
const deleteFloorplan = async (index: number) => {
  actProperty.confirmPrompt().then(() => {
    // Update floorplans array
    floorplan.splice(index, 1);

    // Update Vuex
    setPropertyspecDeep({ path: props.path, data: floorplan })
      .then(() => toasted.success("Floorplan removed"))
      .catch((err: any) => actProperty.displayError(err));
  }).catch(e => {});
};

const uploadFiles = async (files: FileList | null) => {
  try {
    if (null === files || 0 === files.length) {
      return;
    }

    uploadingCount.value = 0;

    // S3 options
    const options: any = {
      contentType: "application/pdf",
      customPrefix: {
        public: "",
        protected: "",
        private: "",
      },
      level: "public",
    };

    for (let i = 0; i < files.length; i++) {
      let file: File | null = files.item(i);

      if (file === null) {
        continue;
      }

      const { name, type, lastModified } = file;

      if (validtypes.indexOf(type) < 0) {
        actProperty.displayError(`${sanitize(name)} is not a valid type.`);
        continue;
      }

      // Increase uploadingCount (displays loading spinner)
      ++uploadingCount.value;

      // Add timestamp to make each device upload unique, and sanitise input filename
      let deviceKeyPrefix = `${deviceKey.value}`;
      if (!deviceKey.value) deviceKeyPrefix = `customerfloorplans`;
      else deviceKeyPrefix = `${deviceKey.value}`;
      const s3Filepath = `${deviceKeyPrefix}/${moment().format(
        "x"
      )}/${slugify(sanitize(name))}`;

      if (file?.type) {
        options.contentType = file.type;
      }

      // Save the File to S3
      await Storage.put(s3Filepath, file, options)
        .then(({ key }: any) => {
          if (!key || !key.length) {
            --uploadingCount.value;
            throw Error("Could not determine uploaded URL");
          }

          // Create the Document from the S3 response
          const floorplan = new Document({
            src: actProperty.s3Origin(key),
            createdAt: actProperty.datetimeToUTC(lastModified),
          });

          // Add Document to floorplans array
          const floorplans = _.get(propertyspec.value, path).concat([
            floorplan,
          ]);

          // Update Vuex
          setPropertyspecDeep({ path: path, data: floorplans });

          --uploadingCount.value;
        })
        .catch((error: Error) => {
          // Adjust uploadingCount and rethrow error
          --uploadingCount.value;
          throw error;
        });
    }
  } catch (error) {
    actProperty.displayError(error);
  }
};

// Lifecycle hooks
onMounted(() => {
  uploadingCount.value = 0;
});

// Event handlers
const onDragOver = (event: DragEvent) => {
  isDragging.value = true;
};

const onClick = (index:number) =>{
  emit('clicked', index);
}

const onDragLeave = (event: MouseEvent) => {
  if (event.currentTarget === event.target) {
    isDragging.value = false;
  }
};

const onDrop = (event: DragEvent) => {
  isDragging.value = false;
  const files: FileList | null = event.dataTransfer?.files || null;
  uploadFiles(files);
};

const onFileInputChange = (event: Event) => {
  const files = _.get(file.value, "files", null);
  uploadFiles(files);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["file-upload", { drag: isDragging.value }]),
    onDragover: _withModifiers(onDragOver, ["stop","prevent"]),
    onDragleave: _withModifiers(onDragLeave, ["stop","prevent"]),
    onDrop: _withModifiers(onDrop, ["stop","prevent"])
  }, [
    _createElementVNode("div", null, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(floorplan), (fp, $index) => {
        return (_openBlock(), _createBlock(FloorplanComponent, {
          key: _unref(actProperty).hashObj(fp),
          floorplan: fp,
          onDelete: ($event: any) => (deleteFloorplan($index)),
          onClick: _withModifiers(($event: any) => (onClick($index)), ["stop"])
        }, null, 8, ["floorplan", "onDelete", "onClick"]))
      }), 128))
    ]),
    (uploadingCount.value)
      ? (_openBlock(), _createElementBlock("span", _hoisted_1, _hoisted_3))
      : _createCommentVNode("", true),
    (!uploadingCount.value && (!_unref(floorplan) || !_unref(floorplan).length))
      ? (_openBlock(), _createElementBlock("label", _hoisted_4, [
          _createElementVNode("span", _hoisted_5, [
            (_unref(buttontext))
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(buttontext)), 1))
              : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _hoisted_8,
                  _createTextVNode(" Upload ")
                ]))
          ]),
          _createElementVNode("input", {
            type: "file",
            accept: "application/pdf,image/jpeg,image/png",
            multiple: "",
            ref_key: "file",
            ref: file,
            onChange: onFileInputChange
          }, null, 544)
        ]))
      : _createCommentVNode("", true)
  ], 34))
}
}

})